import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, GraduationCap, Award, Building, Check, Calendar } from 'lucide-react';
import * as Icons from 'lucide-react';

export function Experience() {
  const { data: portfolioData, isLoading } = usePortfolioData();

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="text-primary text-xl h-5 w-5" />;
  };

  if (isLoading) {
    return (
      <section id="experience" className="py-20 bg-gray-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-40 mx-auto mb-4" />
            <Skeleton className="w-16 h-1.5 mx-auto rounded-full mb-4" />
            <Skeleton className="h-5 w-96 mx-auto" />
          </div>
          
          <div className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-7/12 px-4 mb-8 lg:mb-0">
              <div className="relative pl-10">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="mb-10 h-64 rounded-xl" />
                ))}
              </div>
            </div>
            
            <div className="w-full lg:w-5/12 px-4">
              <Skeleton className="h-72 rounded-xl mb-8" />
              <Skeleton className="h-72 rounded-xl" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!portfolioData) return null;

  return (
    <section id="experience" className="py-20 bg-gray-50 dark:bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Experience</h2>
          <div className="w-16 h-1.5 bg-primary mx-auto rounded-full"></div>
          <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">
            My professional journey and work history.
          </p>
        </div>
        
        <div className="flex flex-wrap -mx-4">
          {/* Experience Timeline */}
          <div className="w-full lg:w-7/12 px-4 mb-8 lg:mb-0">
            <div className="timeline-container relative pl-10">
              {portfolioData.experience.map((job, index) => (
                <div key={index} className="mb-10 relative">
                  {/* Timeline dot */}
                  <div className="absolute -left-10 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                    <Briefcase className="h-4 w-4" />
                  </div>
                  
                  {/* Content */}
                  <Card className="ml-2">
                    <CardContent className="pt-6">
                      <div className="flex flex-wrap items-center justify-between mb-3">
                        <h3 className="text-xl font-semibold">{job.title}</h3>
                        <Badge variant="outline" className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full">
                          {job.period}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center mb-4">
                        <Building className="text-gray-500 mr-2 h-4 w-4" />
                        <span className="text-gray-600 dark:text-gray-300 font-medium">{job.company}</span>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-300 mb-4">{job.description}</p>
                      
                      <div className="flex flex-col gap-2">
                        {job.achievements.map((achievement, i) => (
                          <div key={i} className="flex items-start">
                            <Check className="text-primary mt-1 mr-2 h-4 w-4 shrink-0" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">{achievement}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
          
          {/* Education & Certifications */}
          <div className="w-full lg:w-5/12 px-4">
            <Card className="mb-8">
              <CardHeader>
                <h3 className="text-2xl font-semibold mb-2 flex items-center">
                  <GraduationCap className="text-primary mr-3 h-6 w-6" />
                  Education
                </h3>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {portfolioData.education.map((edu, index) => (
                  <div key={index} className="border-l-4 border-primary pl-4 pb-1">
                    <h4 className="text-lg font-semibold">{edu.degree}</h4>
                    <div className="flex items-center mt-1 mb-2">
                      <GraduationCap className="text-gray-500 mr-2 h-4 w-4" />
                      <span className="text-gray-600 dark:text-gray-300">{edu.institution}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="text-gray-500 mr-2 h-4 w-4" />
                      <span className="text-sm text-gray-500">{edu.period}</span>
                    </div>
                    {edu.details && (
                      <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm">{edu.details}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <h3 className="text-2xl font-semibold mb-2 flex items-center">
                  <Award className="text-primary mr-3 h-6 w-6" />
                  Certifications
                </h3>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {portfolioData.certifications.map((cert, index) => (
                  <div key={index} className="flex">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center shrink-0 mr-4">
                      <DynamicIcon name={cert.icon} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold">{cert.name}</h4>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{cert.issuer} • {cert.date}</p>
                      {cert.url && (
                        <a 
                          href={cert.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-primary text-sm hover:underline inline-flex items-center mt-1"
                        >
                          <span>View Certificate</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Experience;
